import { useCallback, useEffect, useRef, useState } from 'react'
import { getCustomers } from '../api'
import { loadList } from './loadState'

/**
 * The customer list behind every customer picker (queue item 201).
 *
 * Eighteen screens each fetched GET /api/context themselves and each caught a failure
 * its own way -- `catch {}`, `.catch(() => setCustomers([]))`, `console.error` -- so a
 * dropped connection read as "no customers" everywhere at once. This is the one loader:
 *
 *   const { customers, error, loading, reload } = useCustomerList(list => ...)
 *
 * `error` is set (and `customers` is []) when the request failed; render
 * <LoadError what="customers" error={error} onRetry={reload} />. `onLoad` runs after each
 * SUCCESSFUL load only (e.g. to preselect the first customer) -- never on a failure.
 */
export default function useCustomerList(onLoad) {
  const [state, setState] = useState({ customers: [], error: null, loading: true })
  const onLoadRef = useRef(onLoad)
  onLoadRef.current = onLoad

  const reload = useCallback(async () => {
    setState(s => ({ ...s, error: null, loading: true }))
    const { items, error } = await loadList(getCustomers, 'contexts', 'customers')
    setState({ customers: items || [], error, loading: false })
    if (items && onLoadRef.current) onLoadRef.current(items)
  }, [])

  useEffect(() => { reload() }, [reload])

  return { ...state, reload }
}
